import React from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
}) => {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    'full': 'max-w-7xl'
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black bg-opacity-50" />

      <div className={`fixed inset-0 flex items-center justify-center ${size === 'full' ? 'p-0 sm:p-4' : 'p-4 sm:p-6'}`}>
        <Dialog.Panel className={`w-full ${sizeClasses[size]} flex flex-col ${size === 'full' ? 'h-full sm:max-h-[95vh] rounded-none sm:rounded-2xl' : 'max-h-[90vh] rounded-2xl'} transform bg-white text-left align-middle shadow-2xl`}>
          {/* Header - Fixed */}
          <div className="flex items-start justify-between px-3 pt-3 pb-3 sm:px-6 sm:pt-6 sm:pb-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex-1">
              {title && (
                <Dialog.Title
                  as="h3"
                  className="text-xl font-semibold leading-6 text-gray-900 text-center"
                >
                  {title}
                </Dialog.Title>
              )}
              {subtitle && (
                <p className="mt-2 text-sm text-gray-600 text-center">
                  {subtitle}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                className="ml-4 rounded-lg p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-3 py-3 sm:px-6 sm:py-4">
            {children}
          </div>

          {/* Footer - Fixed at bottom */}
          {footer && (
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              {footer}
            </div>
          )}
        </Dialog.Panel>
      </div>
        </Dialog>
      );
};

export default Modal;